/* ============================================================
   Шапка новой витрины: логотип, навигация с дропдаунами,
   переключатель языка, вход/регистрация (или профиль),
   бургер для мобильного меню.
   ============================================================ */
import { useAppStore } from '../../store';
import { useFedStore, useL } from '../store';
import { useFedContent } from '../content';
import { tournamentPhase, sortTournaments, tl } from '../data';
import useTournamentClock from '../useTournamentClock';
import { Ic } from '../icons';
import { FedLogo } from '../ui';
import { messengerUnread, useMessenger } from '../messenger/store';

function NavDropdown({ items }) {
  return (
    <div className="fed-navdd">
      <div
        className="p-2 rounded-[14px] border border-line-2 shadow-card-lg flex flex-col gap-0.5"
        style={{ background: 'linear-gradient(160deg,var(--fed-surface-2),var(--fed-surface))' }}
      >
        {items.map((d, i) => (
          <button
            key={i}
            onClick={d.onClick}
            className="flex items-center justify-between gap-3.5 bg-transparent border-none cursor-pointer rounded-[9px] transition-colors hover:bg-surface-3"
            style={{ padding: '10px 12px' }}
          >
            <span className="font-display font-semibold text-fg whitespace-nowrap" style={{ fontSize: 14, letterSpacing: '.02em' }}>{d.label}</span>
            {d.sub ? (
              <span className="font-mono uppercase whitespace-nowrap" style={{ fontSize: 9, letterSpacing: '.1em', color: d.subColor }}>{d.sub}</span>
            ) : null}
          </button>
        ))}
      </div>
    </div>
  );
}

export default function FedNavBar() {
  const route = useAppStore((s) => s.route);
  const user = useAppStore((s) => s.user);
  const notifications = useAppStore((s) => s.notifications);
  const logout = useAppStore((s) => s.logout);
  const { go, toggleLang, toggleMenu, lang } = useFedStore();
  const L = useL();
  const tournaments = useFedContent((s) => s.tournaments);
  useTournamentClock(tournaments);
  const conversations = useMessenger((s) => s.conversations);

  const unread = (notifications || []).filter((n) => !n.read).length;
  const chatUnread = messengerUnread(conversations);

  // Подпись турнира в выпадашке. Открытый приём заявок важнее статуса: пока
  // идёт регистрация, турнир формально «анонс», но зовём в него, а не просто
  // сообщаем о нём (regOpen есть только у турниров со своей механикой).
  const tourSub = (t) => {
    const phase = tournamentPhase(t);
    if (phase === 'registration') return [lang === 'ru' ? 'регистрация' : 'тіркелу', 'var(--fed-ok)'];
    if (phase === 'closed') return [lang === 'ru' ? 'рег. закрыта' : 'тіркелу жабық', 'var(--fed-accent-bright)'];
    if (phase === 'done') return [lang === 'ru' ? 'завершён' : 'аяқталды', 'var(--fed-fg-faint)'];
    if (phase === 'live') return [lang === 'ru' ? 'идёт' : 'жүріп жатыр', 'var(--fed-ok)'];
    return ['анонс', 'var(--fed-gold)'];
  };

  const ddTours = sortTournaments(tournaments)
    .map((t) => ({
      label: tl(t, 'title', lang),
      sub: tourSub(t)[0],
      subColor: tourSub(t)[1],
      onClick: () => go('tournament', { tourId: t.id, tourTab: 'overview' }),
    }))
    .concat([{ label: L.nav_all_tours, sub: '', subColor: 'var(--fed-accent-bright)', onClick: () => go('tournaments') }]);

  const ddFed = [
    ['about', L.nav_about], ['projects', L.nav_projects], ['winners', L.nav_winners],
    ['partners', L.nav_partners], ['gallery', L.nav_gallery], ['regulations', L.nav_regs], ['contacts', L.nav_contacts],
  ].map(([k, label]) => ({ label, sub: '', onClick: () => go(k) }));

  const nav = [
    { label: L.nav_home, active: route === 'home', onClick: () => go('home') },
    { label: L.nav_tournaments, active: route === 'tournaments' || route === 'tournament', onClick: () => go('tournaments'), dd: ddTours },
    { label: L.nav_news, active: route === 'news' || route === 'article', onClick: () => go('news') },
    { label: L.nav_live, active: route === 'live' || route === 'stream', onClick: () => go('live') },
    { label: lang === 'kz' ? 'Чаттар' : 'Чаты', icon: 'chat', badge: chatUnread, active: route === 'messenger', onClick: () => go('messenger') },
    { label: L.nav_about, active: ['about', 'projects', 'winners', 'partners', 'gallery', 'regulations', 'contacts'].includes(route), onClick: () => go('about'), dd: ddFed },
  ];

  return (
    <header
      className="fed-topbar sticky top-0 flex items-center border-b border-line"
      style={{
        zIndex: 200, height: 72,
        background: 'linear-gradient(180deg,rgba(8,8,10,.94),rgba(8,8,10,.82))',
        backdropFilter: 'blur(16px)', WebkitBackdropFilter: 'blur(16px)',
      }}
    >
      <div className="w-full max-w-[1320px] mx-auto px-6 flex items-center gap-5">
        {/* логотип + бренд */}
        <div onClick={() => go('home')} className="fed-nav-brand flex items-center gap-3 cursor-pointer flex-shrink-0">
          <FedLogo size={44} />
          <div className="fed-nav-brand-copy flex flex-col leading-none">
            <span className="font-display font-bold text-fg" style={{ fontSize: 16, letterSpacing: '.03em' }}>{L.brandTop}</span>
            <span className="font-mono uppercase text-fg-muted mt-1" style={{ fontSize: 9, letterSpacing: '.24em' }}>{L.brandSub}</span>
          </div>
        </div>

        {/* десктоп-навигация */}
        <nav className="fed-nav-desk items-center gap-0.5 ml-auto">
          {nav.map((item, i) => (
            <div key={i} className="fed-navitem flex items-center">
              <button
                onClick={item.onClick}
                className="fed-nav-link relative inline-flex items-center gap-1.5 bg-transparent border-none cursor-pointer font-display font-semibold uppercase text-fg-soft transition-colors hover:text-fg"
                style={{ padding: '8px 12px', fontSize: 14, letterSpacing: '.05em' }}
              >
                {item.icon && <Ic name={item.icon} size={15} sw={1.9} />}{item.label}
                {item.badge > 0 && <span className="msg-nav-badge">{item.badge > 9 ? '9+' : item.badge}</span>}
                {item.dd && (
                  <span className="fed-nav-caret opacity-55" style={{ transition: 'transform .22s var(--fed-ease)', display: 'inline-flex' }}>
                    <Ic name="chevD" size={11} sw={2.6} />
                  </span>
                )}
                {item.active && (
                  <span
                    className="absolute rounded-sm"
                    style={{ left: 12, right: 12, bottom: 2, height: 2, background: 'var(--fed-accent)', boxShadow: '0 0 8px var(--fed-accent-glow)' }}
                  />
                )}
              </button>
              {item.dd && <NavDropdown items={item.dd} />}
            </div>
          ))}
        </nav>

        {/* язык + аккаунт (десктоп) */}
        <div className="fed-nav-desk items-center gap-2.5 flex-shrink-0">
          <button
            onClick={toggleLang}
            title="RU / KZ"
            className="flex items-center h-[34px] border border-line-2 bg-surface-2 rounded-lg overflow-hidden cursor-pointer p-0"
          >
            <span className="px-[9px] h-full flex items-center font-mono font-bold" style={{ fontSize: 11, letterSpacing: '.06em', background: lang === 'ru' ? 'var(--fed-accent)' : 'transparent', color: lang === 'ru' ? '#fff' : 'var(--fed-fg-faint)' }}>РУ</span>
            <span className="px-[9px] h-full flex items-center font-mono font-bold" style={{ fontSize: 11, letterSpacing: '.06em', background: lang === 'kz' ? 'var(--fed-accent)' : 'transparent', color: lang === 'kz' ? '#fff' : 'var(--fed-fg-faint)' }}>ҚАЗ</span>
          </button>

          {!user ? (
            <>
              <button
                onClick={() => go('auth', { authMode: 'login' })}
                className="h-[34px] px-3.5 border border-line-2 bg-transparent text-fg-soft rounded-lg cursor-pointer font-display font-semibold uppercase"
                style={{ fontSize: 13, letterSpacing: '.04em' }}
              >
                {L.login}
              </button>
              <button
                onClick={() => go('auth', { authMode: 'signup' })}
                className="fed-nav-cta-full h-[34px] px-4 border-none text-white rounded-lg cursor-pointer font-display font-bold uppercase"
                style={{ fontSize: 13, letterSpacing: '.04em', background: 'linear-gradient(120deg,var(--fed-accent-bright),var(--fed-accent-deep))', boxShadow: '0 8px 22px -10px var(--fed-accent-glow)' }}
              >
                {L.signup}
              </button>
            </>
          ) : (
            <>
              {user.role === 'admin' || user.user_type === 'admin' ? (
                <button
                  onClick={() => go('admin')}
                  className="h-[34px] px-3.5 border border-line-accent rounded-lg cursor-pointer font-display font-semibold uppercase text-accent-bright"
                  style={{ fontSize: 13, letterSpacing: '.04em', background: 'rgba(225,18,23,.08)' }}
                >
                  Админ
                </button>
              ) : null}
              {user.user_type === 'smm' ? (
                <button
                  onClick={() => go('smm')}
                  className="h-[34px] px-3.5 border border-line-accent rounded-lg cursor-pointer font-display font-semibold uppercase text-accent-bright"
                  style={{ fontSize: 13, letterSpacing: '.04em', background: 'rgba(225,18,23,.08)' }}
                >
                  SMM
                </button>
              ) : null}
              <button
                onClick={() => go('notifications')}
                title="Уведомления"
                className="relative h-[34px] w-[34px] border border-line-2 bg-surface-2 text-fg-soft rounded-lg cursor-pointer flex items-center justify-center"
              >
                <Ic name="bell" size={17} />
                {unread > 0 && (
                  <span className="absolute -top-1.5 -right-1.5 min-w-[16px] h-4 px-1 rounded-full text-white font-mono flex items-center justify-center" style={{ fontSize: 9, background: 'var(--fed-accent)' }}>
                    {unread}
                  </span>
                )}
              </button>
              <button
                onClick={() => go('profile')}
                className="h-[34px] px-3 border border-line-2 bg-surface-2 text-fg rounded-lg cursor-pointer flex items-center gap-2 font-display font-semibold uppercase max-w-[160px]"
                style={{ fontSize: 13 }}
              >
                <Ic name="user" size={15} color="var(--fed-accent-bright)" />
                <span className="truncate">{user.nickname || user.email}</span>
              </button>
              <button onClick={logout} title="Выйти" className="h-[34px] w-[34px] border border-line-2 bg-transparent text-fg-muted rounded-lg cursor-pointer flex items-center justify-center hover:text-accent-bright">
                <Ic name="logout" size={16} />
              </button>
            </>
          )}
        </div>

        {/* быстрые действия в мобильной шапке */}
        <div className="fed-nav-mobile-actions ml-auto items-center gap-2">
          <button onClick={toggleLang} title="RU / KZ" aria-label={L.langLabel} className="fed-mobile-lang h-[38px] border border-line-2 bg-surface-2 rounded-lg overflow-hidden cursor-pointer p-0">
            <span className={lang === 'ru' ? 'on' : ''}>РУ</span>
            <span className={lang === 'kz' ? 'on' : ''}>ҚАЗ</span>
          </button>
          {!user ? (
            <button onClick={() => go('auth', { authMode: 'login' })} className="fed-mobile-account is-guest h-[38px] rounded-lg cursor-pointer gap-2 px-3" aria-label={L.login}>
              <Ic name="user" size={17} color="var(--fed-accent-bright)" /><span>{L.login}</span>
            </button>
          ) : (
            <>
              <button
                onClick={() => go('notifications')}
                className="fed-mobile-notifications relative h-[38px] w-[38px] border border-line-2 bg-surface-2 text-fg rounded-lg cursor-pointer inline-flex items-center justify-center"
                aria-label={lang === 'kz' ? 'Хабарландыруларды ашу' : 'Открыть уведомления'}
              >
                <Ic name="bell" size={17} color={unread > 0 ? 'var(--fed-accent-bright)' : 'var(--fed-fg-soft)'} />
                {unread > 0 && <b>{unread > 9 ? '9+' : unread}</b>}
              </button>
              <button onClick={() => go('profile')} className="fed-mobile-account relative h-[38px] border border-line-2 bg-surface-2 text-fg rounded-lg cursor-pointer gap-2 px-3" aria-label={lang === 'kz' ? 'Профильді ашу' : 'Открыть профиль'}>
                <Ic name="user" size={17} color="var(--fed-accent-bright)" /><span>{user.nickname || user.email}</span>
              </button>
            </>
          )}
        </div>

        {/* бургер (мобилка/планшет) */}
        <button
          onClick={toggleMenu}
          aria-label="Открыть меню"
          className="fed-nav-burger ml-auto w-[42px] h-[42px] items-center justify-center bg-surface-2 border border-line-2 rounded-[9px] cursor-pointer text-fg flex-shrink-0"
        >
          <Ic name="burger" size={20} sw={2} />
        </button>
      </div>
    </header>
  );
}
